<script setup lang="ts">
import { computed } from 'vue'
import { useScreen } from '@/composables/useScreen'
import type { WebinarFilters } from './types'

type FilterItem = {
  id: string
  text: string
  icon: string
  iconColour: string
}

const props = withDefaults(
  defineProps<{
    modelValue?: WebinarFilters
    is_teacher?: boolean
  }>(),
  {
    modelValue: () => ({})
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: WebinarFilters): void
}>()

const { isMobile } = useScreen()

const filterItems = computed<FilterItem[]>(() => [
  { id: 'access_policy:p', text: 'Для родителей', icon: 'mdi-checkbox-blank', iconColour: 'blue' },
  { id: 'access_policy:t', text: 'Для учителей', icon: 'mdi-checkbox-blank', iconColour: 'orange' },
  { id: 'me_coach', text: 'Я организатор', icon: 'mdi-account-circle-outline', iconColour: 'orange' },
  { id: 'purchased', text: 'Я участник', icon: 'mdi-check', iconColour: 'green' },
  { id: 'liked', text: 'Избранное', icon: 'mdi-heart', iconColour: 'red' }
])

const selectedIds = computed(() => {
  const selected: string[] = []
  const value = props.modelValue

  if (value.access_policy === 'p') selected.push('access_policy:p')
  if (value.access_policy === 't') selected.push('access_policy:t')
  if (value.me_coach) selected.push('me_coach')
  if (value.purchased) selected.push('purchased')
  if (value.liked) selected.push('liked')

  return selected
})

const hasActiveFilters = computed(() => selectedIds.value.length > 0)

function toggleItem(item: FilterItem) {
  const next = { ...props.modelValue }
  if (item.id.startsWith('access_policy:')) {
    const val = item.id.split(':')[1]
    next.access_policy = next.access_policy === val ? null : val
  } else {
    next[item.id] = !next[item.id]
  }

  emit('update:modelValue', next)
}

function applySelectedIds(ids: string[]) {
  const selected = Array.isArray(ids) ? ids : []
  const next = { ...props.modelValue }

  selected.forEach(id => {
    if (id.startsWith('access_policy:')) {
      next.access_policy = id.split(':')[1]
    } else {
      next[id] = true
    }
  })

  emit('update:modelValue', next)
}

function clearFilters() {
  emit('update:modelValue', {})
}
</script>

<template>
  <div v-if="isMobile" class="webinar-filters">
    <v-select
      class="webinar-filters__select"
      placeholder="Выбрать фильтры"
      multiselect
      item-text="text"
      item-value="id"
      :items="filterItems"
      :model-value="selectedIds"
      @update:model-value="applySelectedIds"
    >
      <template #append-item="{ item }">
        <span class="flex-spacer" />
        <v-icon :class="[`${item.iconColour}-text`]">{{ item.icon }}</v-icon>
      </template>
    </v-select>
    <button v-if="hasActiveFilters" class="webinar-filters__clear-btn ml-1" @click="clearFilters">
      <v-icon class="blue-text">mdi-close</v-icon>
    </button>
  </div>

  <div v-else class="webinar-filters">
    <div class="webinar-filters__container">
      <button
        v-for="f in filterItems"
        :key="f.id"
        class="webinar-filters__item"
        :class="{ 'webinar-filters__item_inactive': hasActiveFilters && !selectedIds.includes(f.id) }"
        @click="() => toggleItem(f)"
      >
        <v-icon :class="[`${f.iconColour}-text`]">{{ f.icon }}</v-icon>
        <span v-text="f.text" />
      </button>
    </div>

    <transition name="fade">
      <v-button v-if="hasActiveFilters" class="ml-3" mode="small text primary" @click="clearFilters">
        Сбросить
      </v-button>
    </transition>
  </div>
</template>

<style lang="scss">
.webinar-filters {
  display: flex;
  align-items: center;

  // So the clear button on desktop
  // will not stretch the container
  // vertically when appearing
  min-height: 48px;

  &__select {
    flex: 1;
  }

  &__clear-btn {
    cursor: pointer;
  }

  &__container {
    display: flex;
    align-items: center;

    & > * + * {
      margin-left: 32px;
    }

    // Scrollbars
    overflow-y: hidden;

    scrollbar-width: thin;
    scrollbar-color: $white transparent;

    &::-webkit-scrollbar {
      height: 11px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $white;
      border-radius: 6px;
      border: 3px solid white;
    }

    &:hover {
      scrollbar-color: $gray transparent;

      &::-webkit-scrollbar-thumb {
        background-color: $gray;
      }
    }
  }

  &__item {
    display: flex;
    min-width: fit-content;

    cursor: pointer;
    transition: opacity 0.1s ease;

    & > * + * {
      margin-left: 12px;
    }

    &_inactive {
      opacity: 0.5;
    }
  }
}
</style>
